import { useEffect, useRef } from 'react';

import { type TeamInviteCtaClickedLocation } from '@air/analytics';

import { useShowShareLinkTeamInviteNudge } from '~/hooks/useShowShareLinkTeamInviteNudge';

export type UseShowShareLinkTeamInviteNudgeForModalParams = {
  isShareLinkCreated: boolean;
  isShareLinkReady: boolean;
  location: TeamInviteCtaClickedLocation;
  onShareLinkCreatedFallback: (() => void) | undefined;
};

export const useShowShareLinkTeamInviteNudgeForModal = ({
  isShareLinkCreated,
  isShareLinkReady,
  location,
  onShareLinkCreatedFallback,
}: UseShowShareLinkTeamInviteNudgeForModalParams) => {
  const didEvaluateShareLinkTeamInviteNudge = useRef(false);
  const previousIsShareLinkCreatedRef = useRef(false);
  const {
    isShareLinkTeamInviteNudgeEligibilityLoading,
    isShareLinkTeamInviteNudgeVisible,
    openShareLinkTeamInviteNudgeModal,
    showShareLinkTeamInviteNudge,
    workspaceName,
  } = useShowShareLinkTeamInviteNudge({ location });

  useEffect(() => {
    if (isShareLinkCreated && !previousIsShareLinkCreatedRef.current) {
      didEvaluateShareLinkTeamInviteNudge.current = false;
    }

    previousIsShareLinkCreatedRef.current = isShareLinkCreated;
  }, [isShareLinkCreated]);

  useEffect(() => {
    if (
      didEvaluateShareLinkTeamInviteNudge.current ||
      !isShareLinkReady ||
      isShareLinkTeamInviteNudgeEligibilityLoading
    ) {
      return;
    }

    didEvaluateShareLinkTeamInviteNudge.current = true;

    const didShowTeamInviteNudge = showShareLinkTeamInviteNudge();

    if (!didShowTeamInviteNudge && isShareLinkCreated) {
      onShareLinkCreatedFallback?.();
    }
  }, [
    isShareLinkCreated,
    isShareLinkReady,
    isShareLinkTeamInviteNudgeEligibilityLoading,
    onShareLinkCreatedFallback,
    showShareLinkTeamInviteNudge,
  ]);

  return {
    isShareLinkTeamInviteNudgeEligibilityLoading,
    isShareLinkTeamInviteNudgeVisible,
    openShareLinkTeamInviteNudgeModal,
    workspaceName,
  };
};
